/**
 * LO Prospect Pipeline dashboard — funnel overview, search/filter toolbar,
 * sortable table, slide-over drawer, invite modal, transfer dialog, and the
 * compact scenario runner. Built on the shared Lendwise design kit
 * (@/components/lw), which the Recruiting tab shares.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Filter, Layers, MapPin, User, Gauge, X, RotateCcw } from 'lucide-react';
import {
  LO_STAGE_ORDER,
  LO_STAGE_META,
  RUNS_BUCKET_LABEL,
  facetCounts,
  pipelineStats,
  runsBucket,
  type LoProspect,
  type LoProspectStage,
  type LoProspectEditableFields,
  type RunsBucket
} from '@/types/lo-prospect';
import {
  getLoProspects,
  getLoProspect,
  getProspectRecruiters,
  getPricingProfiles,
  getPricingProfilesUiEnabled,
  getManagedUserPricingUiEnabled,
  getManagedUserPricingStatus,
  inviteLoProspect,
  updateLoProspect,
  resetLoProspectRuns,
  sendPreviewInvite,
  restorePreviewInviteSubscription,
  passLoProspect,
  reopenLoProspect,
  transferLoProspectToRecruit,
  type PricingProfileOption,
  type RecruiterOption,
  type NmlsMatch,
  type InviteLoProspectInput,
  type ManagedUserPricingStatus
} from '@/services/loProspectService';
import type { RecruitmentFormValues } from '@/components/recruitment/validations';
import {
  LwThemeScope,
  LwPageHeader,
  LwButton,
  LwSearchInput,
  LwFilterMenu,
  LwFunnel,
  LwTh,
  LwTable,
  LwAvatar,
  LwLicenseBadges,
  LwEmpty,
  type FilterOption
} from '@/components/lw';
import { getErrorMessage } from '@/utils/errorMessage';
import { StageBadge, RunsDots, BestQuote, RecruiterBadge } from './bits';
import { RowMenu, type RowAction } from './pieces';
import { LoProspectDrawer } from './LoProspectDrawer';
import { InviteModal } from './InviteModal';
import { TransferDialog } from './TransferDialog';
import { PassDialog } from './PassDialog';
import { RestoreInviteAccessDialog } from './RestoreInviteAccessDialog';
import { ScenarioRunnerModal } from './ScenarioRunnerModal';
import { compareLoProspectBestQuote } from './sort';

type SortCol = 'name' | 'stage' | 'runs' | 'bestRate' | 'updated';
interface SortState {
  col: SortCol;
  dir: 'asc' | 'desc';
}
interface Filters {
  status: string[];
  state: string[];
  recruiter: string[];
  runs: string[];
}

const relativeTime = (iso: string | null): string => {
  if (!iso) {
    return '—';
  }
  const diff = Date.now() - new Date(iso).getTime();
  const h = diff / 3.6e6;
  if (h < 1) {
    return `${Math.max(1, Math.round(diff / 6e4))}m ago`;
  }
  if (h < 24) {
    return `${Math.round(h)}h ago`;
  }
  const d = Math.round(h / 24);
  return d < 14 ? `${d}d ago` : `${Math.round(d / 7)}w ago`;
};

const ENGAGEMENT_LABEL: Record<NonNullable<LoProspect['lastEngagementType']>, string> = {
  pricing_run: 'Pricing run',
  invite_opened: 'Invite opened',
  nmls_login: 'NMLS login'
};

const engagementTime = (iso: string | null): number | null => {
  if (!iso) {
    return null;
  }
  const time = new Date(iso).getTime();
  return Number.isFinite(time) ? time : null;
};

const formatLastEngagement = (p: LoProspect): string => {
  if (!p.lastEngagementAt || !p.lastEngagementType) {
    return 'No activity';
  }
  return `${ENGAGEMENT_LABEL[p.lastEngagementType]} · ${relativeTime(p.lastEngagementAt)}`;
};

export function LoProspectPipeline({ organizationId, userId }: { organizationId: string; userId: string }) {
  const [prospects, setProspects] = useState<LoProspect[]>([]);
  const [recruiters, setRecruiters] = useState<RecruiterOption[]>([]);
  const [pricingProfiles, setPricingProfiles] = useState<PricingProfileOption[]>([]);
  const [pricingProfilesEnabled, setPricingProfilesEnabled] = useState(false);
  const [pricingProfilesError, setPricingProfilesError] = useState(false);
  const [managedUserPricingEnabled, setManagedUserPricingEnabled] = useState(false);
  const [managedUserPricingStatus, setManagedUserPricingStatus] = useState<
    ManagedUserPricingStatus | 'loading'
  >('flag_off');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [editing, setEditing] = useState<LoProspect | null>(null);
  const [editTab, setEditTab] = useState<'info' | 'pricing'>('info');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [transferTarget, setTransferTarget] = useState<LoProspect | null>(null);
  const [passTarget, setPassTarget] = useState<LoProspect | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<LoProspect | null>(null);
  const [scenarioTarget, setScenarioTarget] = useState<LoProspect | null>(null);
  const editingId = editing?.id ?? null;

  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<Filters>({ status: [], state: [], recruiter: [], runs: [] });
  const [sort, setSort] = useState<SortState>({ col: 'updated', dir: 'desc' });

  const recruiterName = useCallback(
    (id: string | null) => (id ? recruiters.find((r) => r.id === id)?.name ?? null : null),
    [recruiters]
  );
  const defaultInviteRecruiterId = useMemo(
    () => (recruiters.some((r) => r.id === userId) ? userId : null),
    [recruiters, userId]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([getLoProspects(organizationId), getProspectRecruiters(organizationId)]);
      setProspects(p);
      setRecruiters(r);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load prospects'));
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Pricing Profiles: flag-gated in a SEPARATE effect (flag off ⇒ zero extra
  // network). The flag read is global-only + uncached — see the service. A flag
  // read failure hides the selector, matching the backend's env fallback.
  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const enabled = await getPricingProfilesUiEnabled();
        if (!active) {
          return;
        }
        setPricingProfilesEnabled(enabled);
        if (!enabled) {
          return;
        }
        try {
          const profiles = await getPricingProfiles(organizationId);
          if (active) {
            setPricingProfiles(profiles);
          }
        } catch {
          if (active) {
            setPricingProfilesError(true);
          }
        }
      } catch {
        if (active) {
          setPricingProfilesEnabled(false);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [organizationId]);

  // The global flag gates the per-prospect status read below. The pricing and
  // reset RPCs independently verify the current managed-user match and complete
  // OB seat, so UI visibility is never authorization.
  useEffect(() => {
    let active = true;
    void getManagedUserPricingUiEnabled()
      .then((enabled) => {
        if (active) {
          setManagedUserPricingEnabled(enabled);
        }
      })
      .catch(() => {
        if (active) {
          setManagedUserPricingEnabled(false);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  // Resolve only the open prospect, and only after the global rollout flag is
  // on. The browser-safe RPC returns status only—never OB seat identifiers.
  useEffect(() => {
    if (!managedUserPricingEnabled || !editingId) {
      setManagedUserPricingStatus('flag_off');
      return;
    }
    let active = true;
    setManagedUserPricingStatus('loading');
    void getManagedUserPricingStatus(editingId)
      .then((status) => {
        if (active) {
          setManagedUserPricingStatus(status);
        }
      })
      .catch(() => {
        if (active) {
          setManagedUserPricingStatus('error');
        }
      });
    return () => {
      active = false;
    };
  }, [editingId, managedUserPricingEnabled]);

  const refreshOne = useCallback(async (id: string) => {
    const fresh = await getLoProspect(id, organizationId);
    if (fresh) {
      setProspects((ps) => ps.map((p) => (p.id === id ? fresh : p)));
      setEditing((cur) => (cur && cur.id === id ? fresh : cur));
    }
  }, [organizationId]);

  // ---- filtering helpers ----
  const toggleFilter = (facet: keyof Filters, val: string) =>
    setFilters((f) => ({
      ...f,
      [facet]: f[facet].includes(val) ? f[facet].filter((x) => x !== val) : [...f[facet], val]
    }));
  const clearFacet = (facet: keyof Filters) => setFilters((f) => ({ ...f, [facet]: [] }));
  const clearAll = () => {
    setFilters({ status: [], state: [], recruiter: [], runs: [] });
    setQuery('');
  };

  const onSort = (col: SortCol) =>
    setSort((s) => (s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: col === 'bestRate' || col === 'name' ? 'asc' : 'desc' }));

  // ---- derived ----
  const facets = useMemo(() => facetCounts(prospects), [prospects]);
  const stats = useMemo(() => pipelineStats(prospects), [prospects]);

  const matchesQuery = (p: LoProspect): boolean => {
    if (!query) {
      return true;
    }
    const q = query.toLowerCase();
    return `${p.firstName} ${p.lastName}`.toLowerCase().includes(q) || p.nmls.includes(query) || `${p.city ?? ''} ${p.state ?? ''}`.toLowerCase().includes(q);
  };

  const matchNonStatusFilters = (p: LoProspect): boolean => {
    if (filters.state.length && !p.licenses.some((s) => filters.state.includes(s))) {
      return false;
    }
    if (filters.recruiter.length && !filters.recruiter.includes(p.recruiterId || 'unassigned')) {
      return false;
    }
    if (filters.runs.length && !filters.runs.includes(runsBucket(p))) {
      return false;
    }
    return matchesQuery(p);
  };

  // Stage tallies respect every active NON-status constraint (search, State,
  // Recruiter, Runs) so a funnel card / Status option count always equals the
  // rows selecting it reveals — even mid-filter.
  const stageTallies = useMemo(() => {
    const tally: Record<string, number> = {};
    for (const p of prospects) {
      if (matchNonStatusFilters(p)) {
        tally[p.stage] = (tally[p.stage] || 0) + 1;
      }
    }
    return tally;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prospects, query, filters.state, filters.recruiter, filters.runs]);

  const statusOpts: FilterOption[] = LO_STAGE_ORDER.map((k) => ({ value: k, label: LO_STAGE_META[k].label, count: stageTallies[k] || 0 })).filter((o) => o.count > 0);
  const stateOpts: FilterOption[] = Object.keys(facets.state)
    .sort()
    .map((s) => ({ value: s, label: s, count: facets.state[s] }));
  const recruiterOpts: FilterOption[] = [
    ...recruiters.map((r) => ({ value: r.id, label: r.name, count: facets.recruiter[r.id] || 0 })),
    { value: 'unassigned', label: 'Unassigned', count: facets.recruiter['unassigned'] || 0 }
  ].filter((o) => o.count > 0);
  const runsOpts: FilterOption[] = (['none', 'progress', 'full'] as RunsBucket[])
    .map((v) => ({ value: v, label: RUNS_BUCKET_LABEL[v], count: facets.runs[v] || 0 }))
    .filter((o) => o.count > 0);

  const matchFilters = (p: LoProspect): boolean => {
    if (filters.status.length && !filters.status.includes(p.stage)) {
      return false;
    }
    return matchNonStatusFilters(p);
  };

  const pills = [
    ...filters.status.map((v) => ({ facet: 'status' as const, value: v, label: `Status · ${LO_STAGE_META[v as LoProspectStage].label}` })),
    ...filters.state.map((v) => ({ facet: 'state' as const, value: v, label: `State · ${v}` })),
    ...filters.recruiter.map((v) => ({ facet: 'recruiter' as const, value: v, label: `Recruiter · ${v === 'unassigned' ? 'Unassigned' : recruiterName(v) ?? v}` })),
    ...filters.runs.map((v) => ({ facet: 'runs' as const, value: v, label: `Runs · ${RUNS_BUCKET_LABEL[v as RunsBucket]}` }))
  ];
  const activeCount = pills.length + (query ? 1 : 0);

  const rows = useMemo(() => {
    // matchFilters already includes the search query via matchNonStatusFilters.
    let list = prospects.filter(matchFilters);
    const dir = sort.dir === 'asc' ? 1 : -1;
    list = [...list].sort((a, b) => {
      let v = 0;
      switch (sort.col) {
        case 'name':
          v = `${a.lastName}${a.firstName}`.localeCompare(`${b.lastName}${b.firstName}`);
          break;
        case 'runs':
          v = a.runsUsed - b.runsUsed;
          break;
        case 'bestRate': {
          return compareLoProspectBestQuote(a, b, sort.dir);
        }
        case 'stage':
          v = LO_STAGE_ORDER.indexOf(a.stage) - LO_STAGE_ORDER.indexOf(b.stage);
          break;
        case 'updated':
          {
            const at = engagementTime(a.lastEngagementAt);
            const bt = engagementTime(b.lastEngagementAt);
            if (at === null || bt === null) {
              if (at === bt) {
                v = 0;
                break;
              }
              return at === null ? 1 : -1;
            }
            v = at - bt;
          }
          break;
      }
      return v * dir;
    });
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prospects, query, filters, sort]);

  // ---- actions ----
  const openDrawer = (p: LoProspect, tab: 'info' | 'pricing' = 'info') => {
    setManagedUserPricingStatus(managedUserPricingEnabled ? 'loading' : 'flag_off');
    setEditing(p);
    setEditTab(tab);
  };

  const handleAction = async (action: RowAction, p: LoProspect) => {
    switch (action) {
      case 'edit':
        openDrawer(p, 'info');
        break;
      case 'scenarios':
        openDrawer(p, 'pricing');
        break;
      case 'resend':
        setBusy(true);
        try {
          const res = await sendPreviewInvite(p.id);
          if (res.status === 'sent') {
            const updated = await getLoProspect(p.id, organizationId);
            if (updated) {
              setProspects((ps) => ps.map((x) => (x.id === p.id ? updated : x)));
              setEditing((cur) => (cur && cur.id === p.id ? updated : cur));
            }
            const offNote =
              res.publicPreviewLive === false ? ' — note: the preview surface is off until the flag is enabled' : '';
            toast.success(`Preview invite emailed to ${res.email ?? p.firstName}${offNote}`);
          } else if (res.status === 'no_email') {
            toast.error('Add an email to this prospect before sending a preview invite.');
          } else if (res.status === 'email_failed') {
            toast.error('Could not send the invite email — please retry.');
          } else if (res.status === 'disabled') {
            toast.error('The Prospect Pipeline is not enabled.');
          } else if (res.status === 'closed') {
            toast.error('This prospect is no longer in the preview stage.');
          } else if (res.status === 'notification_disabled') {
            toast.error('The preview-invite email is turned off in Email Notifications.');
          } else if (res.status === 'unsubscribed') {
            toast.warning('This recipient unsubscribed. Restore invite access before sending another email.');
          } else {
            toast.error(res.message || 'Could not send the invite.');
          }
        } catch (e) {
          toast.error(getErrorMessage(e, 'Failed to send invite'));
        } finally {
          setBusy(false);
        }
        break;
      case 'restore-subscription':
        setRestoreTarget(p);
        break;
      case 'transition':
        setTransferTarget(p);
        break;
      case 'pass':
        setPassTarget(p);
        break;
      case 'reopen':
        setBusy(true);
        try {
          const updated = await reopenLoProspect(p, userId);
          setProspects((ps) => ps.map((x) => (x.id === p.id ? updated : x)));
          setEditing((cur) => (cur && cur.id === p.id ? updated : cur));
          toast.success(
            `${p.firstName} reopened as ${LO_STAGE_META[updated.stage].label} — preview access stays off until you send an invite`
          );
        } catch (e) {
          toast.error(getErrorMessage(e, 'Failed to reopen prospect'));
        } finally {
          setBusy(false);
        }
        break;
    }
  };

  const handleReset = async (p: LoProspect) => {
    setBusy(true);
    try {
      const updated = await resetLoProspectRuns(p.id);
      setProspects((ps) => ps.map((x) => (x.id === p.id ? updated : x)));
      setEditing((cur) => (cur && cur.id === p.id ? updated : cur));
      const accessGuidance = updated.publicPreviewEnabled
        ? 'They can open portal.lendwisemtg.com/lo-preview and enter their NMLS.'
        : 'Preview access is still off — activate and send an invite before they try again.';
      toast.success(`Runs reset — ${p.firstName} has a fresh 5. ${accessGuidance}`);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to reset runs'));
    } finally {
      setBusy(false);
    }
  };

  const handleInvite = async (input: InviteLoProspectInput) => {
    const result = await inviteLoProspect(organizationId, input, userId);
    if (result.status === 'created') {
      setProspects((ps) => [result.prospect, ...ps]);
      setInviteOpen(false);
      const first = result.prospect.firstName || 'Prospect';
      // Immediately issue + email the preview invite (mints the token + enables access).
      try {
        const sent = await sendPreviewInvite(result.prospect.id);
        if (sent.status === 'sent') {
          const refreshed = await getLoProspect(result.prospect.id, organizationId);
          if (refreshed) {
            setProspects((ps) => ps.map((x) => (x.id === result.prospect.id ? refreshed : x)));
          }
          const offNote =
            sent.publicPreviewLive === false ? ' — the preview surface is off until the flag is enabled' : '';
          toast.success(`Preview invite emailed to ${sent.email ?? first}${offNote}`);
        } else if (sent.status === 'no_email') {
          toast.error(`${first} added. Add an email, then send the preview invite.`);
        } else if (sent.status === 'disabled') {
          toast.error(`${first} added, but the Prospect Pipeline isn’t enabled, so no invite was sent.`);
        } else if (sent.status === 'duplicate') {
          // raced past the creation dedup (they joined Recruiting/Users mid-invite)
          toast.error(`${first} added, but no invite was sent — ${sent.message ?? 'they are already in Recruiting or Users.'}`);
        } else if (sent.status === 'unsubscribed') {
          toast.warning(`${first} was added, but their prior LO Preview opt-out blocked the email.`);
        } else {
          // email_failed / closed / other
          toast.error(`${first} added, but the invite email didn’t send — use “Resend preview invite” to retry.`);
        }
      } catch {
        toast.error(`${first} added, but the invite email didn’t send — use “Resend preview invite” to retry.`);
      }
    }
    return result;
  };

  const openExisting = (match: NmlsMatch) => {
    setInviteOpen(false);
    const found = prospects.find((p) => p.id === match.id);
    if (found) {
      openDrawer(found, 'info');
    }
  };

  const saveProspect = async (fields: LoProspectEditableFields) => {
    if (!editing) {
      return;
    }
    setBusy(true);
    try {
      const updated = await updateLoProspect(editing.id, fields, userId);
      setProspects((ps) => ps.map((p) => (p.id === editing.id ? updated : p)));
      setEditing(null);
      toast.success(`Saved ${updated.firstName} ${updated.lastName}`);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to save'));
    } finally {
      setBusy(false);
    }
  };

  const doTransfer = async (values: RecruitmentFormValues) => {
    if (!transferTarget) {
      return;
    }
    setBusy(true);
    try {
      await transferLoProspectToRecruit(transferTarget.id, values, organizationId, userId);
      setProspects((ps) => ps.map((p) => (p.id === transferTarget.id ? { ...p, stage: 'in_recruiting' } : p)));
      setTransferTarget(null);
      toast.success(`${transferTarget.firstName} ${transferTarget.lastName} moved to the Recruiting Table`);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Transfer failed'));
    } finally {
      setBusy(false);
    }
  };

  // Dialog stays open on failure (only closed on success) so a retry can reuse it.
  const doPass = async (reason?: string) => {
    if (!passTarget) {
      return;
    }
    setBusy(true);
    try {
      const updated = await passLoProspect(passTarget, userId, reason);
      setProspects((ps) => ps.map((x) => (x.id === passTarget.id ? updated : x)));
      setEditing((cur) => (cur && cur.id === passTarget.id ? updated : cur));
      setPassTarget(null);
      toast.success(`${passTarget.firstName} ${passTarget.lastName} marked as passed — preview access is off`);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to mark as passed'));
    } finally {
      setBusy(false);
    }
  };

  const doRestoreInviteAccess = async () => {
    if (!restoreTarget) {
      return;
    }
    setBusy(true);
    try {
      const updated = await restorePreviewInviteSubscription(restoreTarget.id);
      setProspects((ps) => ps.map((x) => (x.id === restoreTarget.id ? updated : x)));
      setEditing((cur) => (cur && cur.id === restoreTarget.id ? updated : cur));
      setRestoreTarget(null);
      toast.success(`${restoreTarget.firstName} can use LO Preview again — no email was sent and old links remain invalid`);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to restore invite access'));
      throw e;
    } finally {
      setBusy(false);
    }
  };

  // Cards show exact current-stage tallies (respecting active search/State/
  // Recruiter/Runs filters) and toggle the same Status filter as the dropdown —
  // the number on a card always equals the rows its click reveals.
  const funnelSteps: { key: LoProspectStage; label: string; sub: string | null; goal?: boolean }[] = [
    { key: 'registered', label: 'Registered', sub: 'awaiting review' },
    { key: 'invited', label: 'Invited', sub: null },
    { key: 'previewing', label: 'Previewing', sub: 'pricing now' },
    { key: 'in_recruiting', label: 'In Recruiting', sub: 'committed', goal: true }
  ];

  return (
    <LwThemeScope className="lw-ds-page">
      <div className="wrap">
        <LwPageHeader
          eyebrow="Partner Pricing Preview"
          title={
            <>
              Prospect <em>Pipeline</em>
            </>
          }
          actions={
            <LwButton onClick={() => setInviteOpen(true)}>
              <Plus size={16} />
              Invite a loan officer
            </LwButton>
          }
        />

        {/* funnel */}
        <LwFunnel
          steps={funnelSteps.map((step) => ({
            key: step.key,
            label: step.label,
            count: stageTallies[step.key] || 0,
            stage: step.key,
            sub: step.sub,
            goal: step.goal,
            active: filters.status.includes(step.key),
            onClick: () => toggleFilter('status', step.key)
          }))}
          kpis={[
            {
              value: (
                <>
                  {stats.runs}
                  <span className="fkpi-sub"> / {stats.runsCap}</span>
                </>
              ),
              label: <>runs used · {stats.pricedCount} of {stats.total} pricing</>
            }
          ]}
        />

        {/* toolbar */}
        <div className="toolbar">
          <LwSearchInput value={query} onChange={setQuery} placeholder="Search name, NMLS, market…" />
          <div className="filter-row">
            <span className="filter-lead">
              <Filter size={15} />
              Filter
            </span>
            <LwFilterMenu label="Status" icon={<Layers size={15} />} options={statusOpts} selected={filters.status} onToggle={(v) => toggleFilter('status', v)} onClear={() => clearFacet('status')} />
            <LwFilterMenu label="State" icon={<MapPin size={15} />} options={stateOpts} selected={filters.state} onToggle={(v) => toggleFilter('state', v)} onClear={() => clearFacet('state')} searchable />
            <LwFilterMenu label="Recruiter" icon={<User size={15} />} options={recruiterOpts} selected={filters.recruiter} onToggle={(v) => toggleFilter('recruiter', v)} onClear={() => clearFacet('recruiter')} />
            <LwFilterMenu label="Runs" icon={<Gauge size={15} />} options={runsOpts} selected={filters.runs} onToggle={(v) => toggleFilter('runs', v)} onClear={() => clearFacet('runs')} />
          </div>
          <div className="spacer" />
          <div className="result-count">
            Showing <b>{rows.length}</b> of {prospects.length}
          </div>
        </div>

        {/* active filters */}
        {activeCount > 0 && (
          <div className="active-filters">
            {query && (
              <span className="afpill" onClick={() => setQuery('')}>
                “{query}”
                <X size={13} />
              </span>
            )}
            {pills.map((p) => (
              <span className="afpill" key={p.facet + p.value} onClick={() => toggleFilter(p.facet, p.value)}>
                {p.label}
                <X size={13} />
              </span>
            ))}
            <button className="clearall" onClick={clearAll} type="button">
              Clear all
            </button>
          </div>
        )}

        {/* table */}
        <LwTable
          footer={
            <>
              <div className="count">
                Showing <b>{rows.length}</b> of {prospects.length} prospects
              </div>
              <div className="legend">
                <span className="legend-item">
                  <span className="rdot used" style={{ width: 9, height: 9 }} />
                  run used
                </span>
                <span className="legend-item">click a row to open the prospect</span>
              </div>
            </>
          }
        >
          <thead>
            <tr>
              <LwTh<SortCol> label="Prospect" col="name" sort={sort} onSort={onSort} />
              <th>Contact</th>
              <th>Licenses</th>
              <LwTh<SortCol> label="Stage" col="stage" sort={sort} onSort={onSort} />
              <LwTh<SortCol> label="Preview runs" col="runs" sort={sort} onSort={onSort} />
              <LwTh<SortCol> label="Best quote" col="bestRate" sort={sort} onSort={onSort} />
              <th>Recruiter</th>
              <LwTh<SortCol> label="Last active" col="updated" sort={sort} onSort={onSort} />
              <th className="th-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={9}>
                  <LwEmpty>Loading prospects…</LwEmpty>
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={9}>
                  <LwEmpty title="No prospects here">Invite a loan officer, or clear your filters.</LwEmpty>
                </td>
              </tr>
            )}
            {!loading &&
              rows.map((p) => (
                <tr className="row" key={p.id} onClick={() => openDrawer(p, 'info')}>
                  <td>
                    <div className="pcell">
                      <LwAvatar first={p.firstName} last={p.lastName} />
                      <div>
                        <div className="pname">
                          {p.firstName} {p.lastName}
                        </div>
                        {p.position && <div className="ppos">{p.position}</div>}
                        <div className="pmeta">
                          <span className="nmls">NMLS #{p.nmls}</span>
                          {(p.city || p.state) && (
                            <>
                              <span className="dotsep" />
                              {p.city}
                              {p.city && p.state ? ', ' : ''}
                              {p.state}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="contact">
                      {p.email && (
                        <a href={`mailto:${p.email}`} onClick={(e) => e.stopPropagation()}>
                          {p.email}
                        </a>
                      )}
                      {p.previewInviteUnsubscribedAt && <span className="pref-chip">Unsubscribed</span>}
                      <span className="ph">{p.phone}</span>
                    </div>
                  </td>
                  <td>
                    <LwLicenseBadges items={p.licenses} />
                  </td>
                  <td>
                    <StageBadge stage={p.stage} />
                  </td>
                  <td>
                    <RunsDots used={p.runsUsed} stage={p.stage} />
                    {p.runsUsed > 0
                      && p.stage !== 'passed'
                      && (p.stage !== 'in_recruiting' || managedUserPricingEnabled)
                      && (
                        <button
                          className="reset-btn"
                          title="Issue a fresh 5 runs (current batch is saved to history)"
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleReset(p);
                          }}
                          type="button"
                        >
                          <RotateCcw size={13} />
                          Reset runs
                        </button>
                      )}
                  </td>
                  <td>
                    <BestQuote prospect={p} />
                  </td>
                  <td>
                    <RecruiterBadge recruiterId={p.recruiterId} recruiterName={recruiterName(p.recruiterId)} />
                  </td>
                  <td>
                    <span className="when">{formatLastEngagement(p)}</span>
                  </td>
                  <td className="actcell" onClick={(e) => e.stopPropagation()}>
                    <RowMenu prospect={p} onAction={(a, pr) => void handleAction(a, pr)} />
                  </td>
                </tr>
              ))}
          </tbody>
        </LwTable>
      </div>

      {editing && (
        <LoProspectDrawer
          prospect={editing}
          recruiters={recruiters}
          pricingProfiles={pricingProfiles}
          pricingProfilesEnabled={pricingProfilesEnabled}
          pricingProfilesError={pricingProfilesError}
          managedUserPricingStatus={managedUserPricingStatus}
          allowInRecruitingReset={managedUserPricingStatus === 'ready'}
          allowInRecruitingPricing={managedUserPricingStatus === 'ready'}
          initialTab={editTab}
          busy={busy}
          onClose={() => setEditing(null)}
          onSave={(fields) => void saveProspect(fields)}
          onReset={() => void handleReset(editing)}
          onRunScenario={() => setScenarioTarget(editing)}
        />
      )}
      {inviteOpen && (
        <InviteModal
          recruiters={recruiters}
          defaultRecruiterId={defaultInviteRecruiterId}
          pricingProfiles={pricingProfiles}
          pricingProfilesEnabled={pricingProfilesEnabled}
          pricingProfilesError={pricingProfilesError}
          onClose={() => setInviteOpen(false)}
          onSubmit={handleInvite}
          onOpenExisting={openExisting}
        />
      )}
      {transferTarget && (
        <TransferDialog
          prospect={transferTarget}
          recruiters={recruiters}
          busy={busy}
          onClose={() => setTransferTarget(null)}
          onSubmit={(values) => void doTransfer(values)}
        />
      )}
      {passTarget && (
        <PassDialog prospect={passTarget} busy={busy} onClose={() => setPassTarget(null)} onSubmit={doPass} />
      )}
      {restoreTarget && (
        <RestoreInviteAccessDialog
          prospect={restoreTarget}
          busy={busy}
          onClose={() => setRestoreTarget(null)}
          onSubmit={doRestoreInviteAccess}
        />
      )}
      {scenarioTarget && (
        <ScenarioRunnerModal
          prospectId={scenarioTarget.id}
          onClose={() => setScenarioTarget(null)}
          onComplete={() => {
            const id = scenarioTarget.id;
            setScenarioTarget(null);
            void refreshOne(id);
          }}
          onToast={(m) => toast.success(m, { id: 'lo-prospect-pricing' })}
        />
      )}
    </LwThemeScope>
  );
}
